import {Box, Card, CardContent, Chip, Grid2, Tooltip, Typography, Button} from "@mui/material";
import React, {useState} from "react";
import {useEffectiveSettings, SETTING} from "../../hooks/use-effective-settings";
import {isSupplementalRiskFactor} from "../../helpers/crimeScore";
import {NotificationTypes, RiskReportTypes} from "../../types";
import {calculateAge} from "../../helpers";
import {
    Save,
    PictureAsPdf,
    Share,
    ArrowBackOutlined,
    NotificationsActive
} from '@mui/icons-material';
import {LoadingButton} from "@mui/lab";

interface Props {
    assessmentData : RiskReportTypes.RiskResponse;
    // Undefined in the download render (the parent passes it with a `!`), which
    // is what hides the action buttons there.
    handleDownload ?: (report:RiskReportTypes.RiskResponse) => void | Promise<void>;
    setCurrentView : (view:'search' | 'details') => void;
    isView : boolean;
    isLoading : boolean;
    notifiedUsers ?: NotificationTypes.NotifiedUser[];
    setOpenNotifyDialog : (flag:boolean) => void;
    setOpenSaveConfirmationDialog : (flag:boolean) => void;
    saveReports : (users:[],isNotify:boolean) => void;
    // Reopened from All Searches: leaving doesn't discard it, so no save prompt.
    isPastSearch ?: boolean;
}


const getUniqueSeriousCharges = (riskFactors: string[]): string[] => {
    return Array.from(
        new Set(riskFactors)
    );
}

export const RiskPatientInfoPanel: React.FC<Props> = ({
                                                          assessmentData,
                                                          handleDownload,
                                                          isView,
                                                          isLoading,
                                                          notifiedUsers,
                                                          setCurrentView,
                                                          setOpenNotifyDialog,
                                                          setOpenSaveConfirmationDialog,
                                                          saveReports,
                                                          isPastSearch
}) => {
    const { riskAssessmentInfo, AreaCrimeScore, sexOffenderNearby, sexOffenderAtLocation } = assessmentData;
    // Both `findings` and `searchedPerson` are absent on some stored reports, and
    // every use below dereferences them directly — see news-articles.tsx.
    let { searchedPerson, findings, riskFactors } = riskAssessmentInfo ?? {};
    // News-derived factors are supplemental (see news-articles.tsx) and are
    // kept out of the headline count so it matches the Key Risk Factors total.
    riskFactors = riskFactors?.length ? getUniqueSeriousCharges(riskFactors).filter((factor) => !isSupplementalRiskFactor(factor)) : [];
    const { addressInfo } = findings ?? {};
    const fullName = `${searchedPerson?.firstName ?? ''} ${searchedPerson?.middleName ? searchedPerson.middleName + ' ' : ''}${searchedPerson?.lastName ?? ''}`.trim();
    const patientAge = calculateAge(searchedPerson?.dateOfBirth) ? calculateAge(searchedPerson?.dateOfBirth) : '--';
    const dob = searchedPerson?.dateOfBirth ? new Date(searchedPerson.dateOfBirth + 'T00:00:00').toLocaleDateString() : '--';

    const [alreadySaved, setAlreadySaved] = useState<boolean>(false);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const { getSetting } = useEffectiveSettings();
    const isIraStandalone = getSetting<string>(SETTING.organizationType, 'full') === 'iraStandalone';

    const handlePdfClick = async () => {
        if (isDownloading || !handleDownload) return;
        setIsDownloading(true);
        try {
            await Promise.resolve(handleDownload(assessmentData));
        } catch (err) {
            // The parent owns the user-facing toast; this is a safety net so a
            // throw before its own try/catch never becomes an unhandled rejection.
            console.error('[handlePdfClick] PDF download error:', err);
        } finally {
            setIsDownloading(false);
        }
    };

    const handleBack = () => {
        let saveDialogDisabled = localStorage.getItem('dontShowSaveReportDialog');
        if(alreadySaved || isView || isPastSearch || saveDialogDisabled === 'true'){
            setCurrentView("search")
        }else{
            setOpenSaveConfirmationDialog(true);
        }
    }

    return(
        <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 4 }}>
                <Grid2 container spacing={1} sx={{ alignItems: 'flex-start' }} justifyContent={'space-between'}>
                    {/* Patient Details */}
                    <Grid2 size={{ xs: 12, md: 2 }}>
                        <Typography variant="h5" fontWeight={500} sx={{ mb: 1, textTransform : 'uppercase' }}>
                            {fullName ?? 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            DOB: {dob} (Age {patientAge ?? 'N/A'})
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {searchedPerson?.address}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Generated {riskAssessmentInfo?.timestamp ? new Date(riskAssessmentInfo.timestamp).toLocaleString() : '--'}
                        </Typography>
                    </Grid2>

                    {/* Statistics Grid */}
                    <Grid2 size={{xs : 12, md : 10}}>
                        <Grid2 mt={4} container spacing={2} justifyContent={'right'}>
                            <Grid2 size={{md:2.2}}>
                                <Card sx={{ display:'flex', justifyContent : 'center', alignItems : 'center', px:1, py:1, height :'105px'}}>
                                    <Box sx={{textAlign : 'center'}}>
                                        <Typography fontWeight={700} fontSize={20} sx={{
                                            color: `${riskFactors?.length>0 ? '#D32F2F' : '#206FA5'}`,
                                            mb : '4px',
                                            lineHeight : 1
                                        }}>
                                            {riskFactors?.length || 0}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.primary"
                                            sx={{
                                                textTransform: 'uppercase',
                                                fontSize: '0.65rem',
                                                letterSpacing: 0.5
                                            }}
                                        >
                                            Risk Factors
                                        </Typography>
                                    </Box>
                                </Card>
                            </Grid2>
                            <Grid2 size={{md:2.2}}>
                                <Card sx={{ display:'flex', justifyContent : 'center', alignItems : 'center', px:2, py:2, height :'105px'}}>
                                    <Box sx={{textAlign : 'center'}}>
                                        <Typography fontWeight={700} fontSize={20} sx={{
                                            color: '#206FA5',
                                            mb : '4px',
                                            lineHeight : 1
                                        }}>
                                            {sexOffenderNearby || 0}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.primary"
                                            sx={{
                                                textTransform: 'uppercase',
                                                fontSize: '0.65rem',
                                                letterSpacing: 0.5
                                            }}
                                        >
                                            Nearby Offenders
                                        </Typography>
                                    </Box>
                                </Card>
                            </Grid2>
                            <Grid2 size={{md:2.2}}>
                                <Card sx={{ display:'flex', justifyContent : 'center', alignItems : 'center', px:2, py:2, height :'105px'}}>
                                    <Box sx={{textAlign : 'center'}}>
                                        <Typography fontWeight={700} fontSize={20} sx={{
                                            color: '#206FA5',
                                            mb : '4px',
                                            lineHeight : 1
                                        }}>
                                            {sexOffenderAtLocation?.length || 0}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.primary"
                                            sx={{
                                                textTransform: 'uppercase',
                                                fontSize: '0.65rem',
                                                letterSpacing: 0.5
                                            }}
                                        >
                                            At Location
                                        </Typography>
                                    </Box>
                                </Card>
                            </Grid2>
                            <Grid2 size={{md:2.2}}>
                                <Card sx={{ display:'flex', justifyContent : 'center', alignItems : 'center', px:2, py:2, height :'105px'}}>
                                    <Box sx={{textAlign : 'center'}}>
                                        <Typography fontWeight={700} fontSize={20} sx={{
                                            color: '#206FA5',
                                            mb : '4px',
                                            lineHeight : 1
                                        }}>
                                            {addressInfo?.propertyInfo?.address?.areaSafetyRating || 'N/A'}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.primary"
                                            sx={{
                                                textTransform: 'uppercase',
                                                fontSize: '0.65rem',
                                                letterSpacing: 0.5
                                            }}
                                        >
                                            Area Safety
                                        </Typography>
                                    </Box>
                                </Card>
                            </Grid2>
                            <Grid2 size={{md:2.2}}>
                                <Card sx={{ display:'flex', justifyContent : 'center', alignItems : 'center', px:2, py:2, height :'105px'}}>
                                    <Box sx={{textAlign : 'center'}}>
                                        <Typography fontWeight={700} fontSize={20} sx={{
                                            color: '#206FA5',
                                            mb : '4px',
                                            lineHeight : 1
                                        }}>
                                            {AreaCrimeScore ? AreaCrimeScore +' %' : 'N/A'}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.primary"
                                            sx={{
                                                textTransform: 'uppercase',
                                                fontSize: '0.65rem',
                                                letterSpacing: 0.5
                                            }}
                                        >
                                            Crime Rate
                                        </Typography>
                                    </Box>
                                </Card>
                            </Grid2>
                        </Grid2>
                    </Grid2>
                </Grid2>

                {/* Action Buttons */}
                {handleDownload && (
                    <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                        <Button variant="outlined-alternate" onClick={() => handleBack()} startIcon={<ArrowBackOutlined />}>
                            Back
                        </Button>
                        {!isView && (
                            <LoadingButton loading={isLoading} variant="alternate" onClick={()=>{saveReports([],false);setAlreadySaved(true)}} startIcon={<Save />}>
                                Save
                            </LoadingButton>
                        )}
                        <LoadingButton loading={isDownloading} variant="outlined-alternate" onClick={handlePdfClick} startIcon={<PictureAsPdf />}>
                            PDF
                        </LoadingButton>
                        {isIraStandalone ? (
                            // Notify fans the report out to mobile app users, which an
                            // IRA-standalone org doesn't have. Greyed rather than hidden
                            // so the capability is visible; the tooltip explains it.
                            <Tooltip title="Notify sends this report to your team's POM mobile app users. Included with the full POM platform.">
                                <span>
                                    <Button variant="outlined-alternate" disabled startIcon={<Share />}>
                                        Notify
                                    </Button>
                                </span>
                            </Tooltip>
                        ) : (
                            <Button variant="outlined-alternate" onClick={()=>{setOpenNotifyDialog(true)}} startIcon={<Share />}>
                                Notify
                            </Button>
                        )}
                    </Box>
                )}

                {/* Who has been notified about this report (cumulative across saves) */}
                {!!notifiedUsers?.length && (
                    <Box className={'pdf-avoid-break'} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                        <Tooltip title="This report was sent to these users' POM mobile app">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'default' }}>
                                <NotificationsActive sx={{ fontSize: 16, color: '#637381' }} />
                                <Typography sx={{ fontSize: '10.5px', fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#919EAB' }}>
                                    Notified
                                </Typography>
                            </Box>
                        </Tooltip>
                        {notifiedUsers.map((user) => (
                            <Tooltip
                                key={user.id}
                                title={`This report was sent to ${user.name}'s POM mobile app${user.notifiedAt ? ` on ${new Date(user.notifiedAt).toLocaleString()}` : ''}`}
                            >
                                <Chip label={user.name} size="small" variant="outlined" className={'text-download-adjust'} />
                            </Tooltip>
                        ))}
                    </Box>
                )}
            </CardContent>
        </Card>
    )
}